import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { fetchAnalytics } from '../analytics/analyticsSlice';
import { fetchOnboardingProgress } from '../onboarding/onboardingSlice';
import { fetchOrders } from '../orders/ordersSlice';
import { fetchProducts } from '../products/productSlice';
import { fetchCustomers } from '../customers/customersSlice';
import { fetchMyStore } from '../store/storeSlice';
import Shell from './Shell';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import { usePermission } from '../roles/RoleGuard';
import useExpiringProducts from '../finance/hooks/useExpiringProducts';
import KYCBadge from '../../components/KYCBadge';
import VirtualAccountCardWithGate from '../../components/VirtualAccountCard';
import StoreActivationSidebar from '../../components/StoreActivationSidebar';
import { Link } from 'react-router-dom';
import {
  Calendar,
  MapPin,
  DollarSign,
  TrendingUp,
  ShoppingCart,
  Users,
  Package,
  Eye,
  Clock,
  CheckCircle,
  RefreshCcw,
  Percent,
  Globe,
  Store as StoreIcon,
  AlertTriangle,
  PackageCheck,
  Wallet,
  Truck,
  Tag,
  ClipboardList,
  ExternalLink,
  Star
} from 'lucide-react';
import { getStoreUrl } from '../../config/platformConfig';
import './Dashboard.css';

/**
 * Stat card — Salesive-style. White surface, 1px gray border, ~12px radius.
 * The icon sits in a softly-tinted circle whose hue identifies the metric
 * (revenue=primary, orders=blue, customers=pink, etc.). NO pastel-wash
 * full-card gradient — that pattern looked dated and the brand is in the
 * icon, not the whole card.
 *
 * Pass `tint` as the metric's brand hue (one of: 'primary', 'blue',
 * 'orange', 'pink'). Default 'primary'. The icon prop is rendered with
 * `currentColor`-style theming via the wrapper.
 */
const TINT_STYLES = {
  primary: { bg: 'rgba(13, 116, 99, 0.10)',   fg: '#0D7463' },
  blue:    { bg: 'rgba(37, 99, 235, 0.10)',   fg: '#2563EB' },
  orange:  { bg: 'rgba(245, 158, 11, 0.12)',  fg: '#B45309' },
  pink:    { bg: 'rgba(236, 72, 153, 0.10)',  fg: '#DB2777' },
};

/**
 * Quick action row — clean flat replacement for the old gradient-icon
 * mini-cards. Tinted circle icon on the left, title + subtitle stacked on
 * the right. Click-or-link via `to` (internal) or `href` (external).
 */
const QuickActionRow = ({ icon, title, subtitle, to, href, tint = 'primary' }) => {
  const style = TINT_STYLES[tint] || TINT_STYLES.primary;
  const inner = (
    <>
      <span className="dash-quick-icon" style={{ backgroundColor: style.bg, color: style.fg }}>
        {icon}
      </span>
      <span className="dash-quick-body">
        <span className="dash-quick-title">{title}</span>
        {subtitle && <span className="dash-quick-subtitle">{subtitle}</span>}
      </span>
    </>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="dash-quick-row">
        {inner}
      </a>
    );
  }
  return (
    <a href={to} className="dash-quick-row">
      {inner}
    </a>
  );
};

const MetricCard = ({ title, value, subtitle, icon, tint = 'primary' }) => {
  const style = TINT_STYLES[tint] || TINT_STYLES.primary;
  return (
    <div className="metric-card">
      <div
        className="metric-card-icon-circle"
        style={{ backgroundColor: style.bg, color: style.fg }}
      >
        {icon}
      </div>
      <div className="metric-card-body">
        <div className="metric-card-title">{title}</div>
        <div className="metric-card-value">{value}</div>
        {subtitle && <div className="metric-card-subtitle">{subtitle}</div>}
      </div>
    </div>
  );
};

// Compact currency formatter for large numbers (K, M, B) with tooltip for full amount
const formatCompactCurrency = (value, currencySymbol = '₦', decimals = 2) => {
  const num = Number(value) || 0;
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 1000000000) {
    return `${sign}${currencySymbol}${(absNum / 1000000000).toFixed(1)}B`;
  } else if (absNum >= 1000000) {
    return `${sign}${currencySymbol}${(absNum / 1000000).toFixed(1)}M`;
  } else if (absNum >= 1000) {
    return `${sign}${currencySymbol}${(absNum / 1000).toFixed(1)}k`;
  }
  return `${currencySymbol}${num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
};

// Component that shows compact currency with a stylish tooltip for full amount on hover
// Accepts currencySymbol and decimals as props (passed from parent that has access to context)
const CurrencyWithTooltipBase = ({ value, currencySymbol = '₦', decimals = 2, style = {}, className = '' }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);

  const compactValue = formatCompactCurrency(value, currencySymbol, decimals);
  const fullValue = `${currencySymbol}${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  const isCompact = compactValue !== fullValue;

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        cursor: isCompact ? 'help' : 'default',
        ...style
      }}
      className={className}
      onMouseEnter={() => isCompact && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {compactValue}
      {showTooltip && isCompact && (
        <span
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            color: '#ffffff',
            padding: '8px 12px',
            borderRadius: '8px',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semibold)',
            whiteSpace: 'nowrap',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            animation: 'tooltipFadeIn 0.2s ease',
          }}
        >
          {fullValue}
          <span
            style={{
              position: 'absolute',
              bottom: '-5px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid rgba(17, 24, 39, 0.95)',
            }}
          />
        </span>
      )}
    </span>
  );
};

const getStatusColor = (status) => {
  switch (status) {
    case 'delivered': return '#10b981';
    case 'shipped': return '#3b82f6';
    case 'processing': return '#f59e0b';
    case 'confirmed': return '#8b5cf6';
    case 'pending': return '#6b7280';
    case 'cancelled': return '#ef4444';
    case 'refunded': return '#ef4444';
    default: return '#6b7280';
  }
};

export default function Dashboard() {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSecureAuth();
  const { displayCurrency, formatMoney, getCurrencyInfo, formatInDisplayCurrency, accountCurrency, convertAmount } = useCurrency();
  const store = useSelector(s => s.store.current);
  const products = useSelector(s => s.products.products);
  const orders = useSelector(s => s.orders.items);
  const customers = useSelector(s => s.customers.items);
  const analytics = useSelector(s => s.analytics.data);
  const { progress: onboardingProgress, steps: onboardingSteps } = useSelector(s => s.onboarding);
  const toast = useToast();

  /* Finance — Expiring Soon dashboard tile. Hook is unconditional so
     React doesn't get upset about hook order, but the `enabled` flag
     short-circuits the fetch so non-finance:view users don't trigger
     wasted 403'd requests on every dashboard render. */
  const canSeeFinance = usePermission('finance:view');
  const { counts: expiringCounts } = useExpiringProducts({ days: 60, enabled: canSeeFinance });

  // Get currency symbol for compact display
  const currencySymbol = getCurrencyInfo(displayCurrency)?.symbol || '₦';
  const currencyDecimals = getCurrencyInfo(displayCurrency)?.decimalPlaces ?? 2;

  // Wrapper component that converts and displays currency values
  // Values are stored in accountCurrency (e.g., NGN) and converted to displayCurrency (e.g., USD)
  const CurrencyWithTooltip = ({ value, style = {}, className = '' }) => {
    // Convert value from account currency to display currency
    const convertedValue = accountCurrency !== displayCurrency
      ? convertAmount(value, accountCurrency, displayCurrency)
      : value;

    return (
      <CurrencyWithTooltipBase
        value={convertedValue}
        currencySymbol={currencySymbol}
        decimals={currencyDecimals}
        style={style}
        className={className}
      />
    );
  };

  const [showStoreModal, setShowStoreModal] = React.useState(false);
  const [isCreatingStore, setIsCreatingStore] = React.useState(false);
  const [storeSlug, setStoreSlug] = React.useState('');
  const [slugError, setSlugError] = React.useState('');
  const [isCheckingSlug, setIsCheckingSlug] = React.useState(false);

  // Activation sidebar - shows for new stores
  const [showActivationSidebar, setShowActivationSidebar] = React.useState(false);

  // Location filter state
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  // Get location data from analytics
  const locations = analytics?.locations?.available || [];
  const hasMultipleLocations = analytics?.locations?.hasMultipleLocations || false;
  const currentLocation = analytics?.locations?.current;
  const isTeamMember = analytics?.locations?.isTeamMember || false;

  // Auto-select first location for team members when locations are loaded
  useEffect(() => {
    if (isTeamMember && locations.length > 0 && !selectedLocationId) {
      // Team member must have a location selected - default to primary or first
      const primaryLocation = locations.find(l => l.isPrimary);
      const defaultLocation = primaryLocation || locations[0];
      if (defaultLocation) {
        setSelectedLocationId(defaultLocation.id);
      }
    }
  }, [isTeamMember, locations, selectedLocationId]);

  useEffect(() => {
    if (isAuthenticated && user) {
      dispatch(fetchAnalytics({ locationId: selectedLocationId }));
      dispatch(fetchOnboardingProgress());
      dispatch(fetchOrders());
      dispatch(fetchProducts());
      dispatch(fetchCustomers());
      dispatch(fetchMyStore());
    }
  }, [isAuthenticated, user, dispatch, selectedLocationId]);

  // Show activation sidebar for merchants - encourages sharing store
  useEffect(() => {
    if (store && store.slug) {
      const permanentlyDismissed = localStorage.getItem('storeActivationDismissed') === 'true';
      const lastDismissedDate = localStorage.getItem('storeActivationDismissedDate');
      const today = new Date().toDateString();

      // Check if dismissed today (daily dismissal)
      const dismissedToday = lastDismissedDate === today;

      // Show if not permanently dismissed and not dismissed today
      if (!permanentlyDismissed && !dismissedToday) {
        const storeCreatedAt = new Date(store.createdAt);
        const daysSinceCreation = (Date.now() - storeCreatedAt.getTime()) / (1000 * 60 * 60 * 24);

        // Show sidebar if store is less than 30 days old (encourage sharing early on)
        if (daysSinceCreation <= 30) {
          setShowActivationSidebar(true);
        }
      }
    }
  }, [store]);

  // Handle location change
  const handleLocationChange = (locationId) => {
    setSelectedLocationId(locationId || null);
  };

  // Auto-generate slug suggestion based on username
  useEffect(() => {
    if (showStoreModal && user?.username && !storeSlug) {
      const suggested = user.username.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      setStoreSlug(suggested);
    }
  }, [showStoreModal, user, storeSlug]);

  // Validate slug format
  const validateSlugFormat = (slug) => {
    if (!slug || slug.trim() === '') {
      return 'Store slug is required';
    }
    if (slug.length < 3) {
      return 'Store slug must be at least 3 characters';
    }
    if (slug.length > 50) {
      return 'Store slug must be less than 50 characters';
    }
    if (!/^[a-z0-9-]+$/.test(slug)) {
      return 'Store slug can only contain lowercase letters, numbers, and hyphens';
    }
    if (slug.startsWith('-') || slug.endsWith('-')) {
      return 'Store slug cannot start or end with a hyphen';
    }
    if (slug.includes('--')) {
      return 'Store slug cannot contain consecutive hyphens';
    }
    return null;
  };

  // Check slug availability
  const checkSlugAvailability = async (slug) => {
    const formatError = validateSlugFormat(slug);
    if (formatError) {
      setSlugError(formatError);
      return false;
    }

    setIsCheckingSlug(true);
    setSlugError('');

    try {
      const response = await fetch(`/api/stores/check-slug?slug=${encodeURIComponent(slug)}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (!data.available) {
        setSlugError('This store slug is already taken. Please choose another one.');
        return false;
      }

      setSlugError('');
      return true;
    } catch (error) {
      console.error('Slug check error:', error);
      setSlugError('Could not verify slug availability. Please try again.');
      return false;
    } finally {
      setIsCheckingSlug(false);
    }
  };

  // Handle slug input change
  const handleSlugChange = (e) => {
    let value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setStoreSlug(value);

    // Clear error on change
    if (slugError) {
      setSlugError('');
    }
  };

  // Handle slug blur (check availability)
  const handleSlugBlur = async () => {
    if (storeSlug) {
      await checkSlugAvailability(storeSlug);
    }
  };

  const handleCreateStore = async () => {
    // Validate before submitting
    const isValid = await checkSlugAvailability(storeSlug);
    if (!isValid) {
      return;
    }

    setIsCreatingStore(true);
    try {
      const response = await fetch('/api/stores/init', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ slug: storeSlug })
      });

      const data = await response.json();

      if (data.success) {
        // Refresh store data
        dispatch(fetchMyStore());
        setShowStoreModal(false);
        toast.success('Store created successfully! Your storefront is now ready.');
      } else {
        toast.error('Failed to create store: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Store creation error:', error);
      toast.error('Failed to create store. Please try again.');
    } finally {
      setIsCreatingStore(false);
    }
  };

  return (
    <Shell>
      <div className="dashboard-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <div className="dashboard-header-title">
              <h1>Welcome</h1>
              <KYCBadge />
            </div>
            <p>Here is a summary of your store's performance.</p>
          </div>

          {/* Location Filter - Only show when merchant has multiple locations */}
          {hasMultipleLocations && locations.length > 0 && (
            <div className="dashboard-location-filter">
              <MapPin size={16} className="location-icon" />
              <select
                value={selectedLocationId || ''}
                onChange={(e) => handleLocationChange(e.target.value)}
                className="location-select"
              >
                {/* Only show "All Locations" option for store owners, not team members */}
                {!isTeamMember && <option value="">All Locations</option>}
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name} {location.isPrimary ? '(Primary)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Current location indicator */}
        {currentLocation && (
          <div className="dashboard-location-indicator">
            <MapPin size={14} />
            <span>Showing analytics for: <strong>{currentLocation.name}</strong></span>
            {/* Only show clear filter for owners, not team members */}
            {!isTeamMember && (
              <button
                onClick={() => handleLocationChange(null)}
                className="clear-location-filter"
              >
                Clear filter
              </button>
            )}
          </div>
        )}

        {/* Setup prompts, KYC progress, and onboarding nudges all moved to
            /overview — Dashboard is now KPIs/operations only. */}

        {/* Key Metrics Grid */}
        <div className="dashboard-metrics-grid">
          <MetricCard
            title="Total Revenue"
            value={<CurrencyWithTooltip value={analytics?.totalRevenue || 0} />}
            subtitle={`${analytics?.totalOrders || 0} orders`}
            icon={<DollarSign size={20} strokeWidth={2.25} />}
            tint="primary"
          />
          <MetricCard
            title="Net Profit"
            value={<CurrencyWithTooltip value={analytics?.netProfit || 0} />}
            subtitle={`${analytics?.profitMargin ? analytics.profitMargin.toFixed(1) : 0}% margin`}
            icon={<TrendingUp size={20} strokeWidth={2.25} />}
            tint="blue"
          />
          <MetricCard
            title="Avg Order Value"
            value={<CurrencyWithTooltip value={analytics?.averageOrderValue || 0} />}
            subtitle="Per transaction"
            icon={<ShoppingCart size={20} strokeWidth={2.25} />}
            tint="orange"
          />
          <MetricCard
            title="Total Customers"
            value={analytics?.totalCustomers || customers?.length || 0}
            subtitle={<span>{analytics?.customerLifetimeValue ? <CurrencyWithTooltip value={analytics.customerLifetimeValue} /> : 'N/A'} CLV</span>}
            icon={<Users size={20} strokeWidth={2.25} />}
            tint="pink"
          />
        </div>

        {/* Secondary metrics row removed — was 6 small stat boxes (Products,
            Store Visits, Pending Settlement, Settled Amount, Refunded,
            Conversion Rate). The hero 4 above + Financial Summary card
            below already cover the same ground. Less noise = better focus. */}

        {/* Expiring Soon tile — only when the merchant has finance:view
            AND at least one product with an expiry date is tracked.
            Urgency hierarchy is carried by the icon-circle tint and the
            count colour — no heavy left rail (over-emphasised the card
            and didn't match the rest of the dashboard's flat chrome). */}
        {canSeeFinance && expiringCounts && expiringCounts.total > 0 && (() => {
          const urgency =
            expiringCounts.urgent > 0  ? { bg: 'rgba(220, 38, 38, 0.10)', fg: '#dc2626' }
          : expiringCounts.warning > 0 ? { bg: 'rgba(217, 119, 6, 0.10)', fg: '#d97706' }
                                       : { bg: 'rgba(5, 150, 105, 0.10)', fg: '#059669' };
          return (
            <Link
              to="/finance?tab=expiring"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: 12,
                padding: 14,
                textDecoration: 'none',
                color: 'inherit',
                marginBottom: 'var(--space-4)',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
              }}
              aria-label="View expiring stock in Finance"
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: urgency.bg,
                  color: urgency.fg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
                aria-hidden
              >
                <Calendar size={20} strokeWidth={2} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 2 }}>
                  Expiring soon
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  {expiringCounts.urgent > 0 && (
                    <>
                      <strong style={{ color: urgency.fg }}>{expiringCounts.urgent}</strong>
                      {' '}{expiringCounts.urgent === 1 ? 'product' : 'products'} in 7 days
                    </>
                  )}
                  {expiringCounts.urgent > 0 && expiringCounts.total > expiringCounts.urgent && ' · '}
                  {expiringCounts.total > expiringCounts.urgent && (
                    <>
                      <strong style={{ color: '#0f172a' }}>{expiringCounts.total}</strong>
                      {' '}in 60 days
                    </>
                  )}
                </div>
              </div>
              <span style={{ fontSize: 13, color: '#0d7463', fontWeight: 600, whiteSpace: 'nowrap' }}>
                View all →
              </span>
            </Link>
          );
        })()}

        {/* Sales Channels & Financial Performance */}
        <div className="dashboard-sales-grid">
          {/* Sales Channels with DVA */}
          <Card>
            <Card.Header>
              <Card.Title>Sales Channels</Card.Title>
              <Card.Description>Revenue by sales channel</Card.Description>
            </Card.Header>

            <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
              {/* Sales Channel Stats */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', flex: 1 }}>
              {/* Channel rows — flat, white-on-white with a hairline border.
                  Stripped the loud 2px tinted borders + gradient % circles —
                  the percentage now reads as a small muted figure top-right. */}
              <div className="dash-channel-row">
                <div className="dash-channel-row-body">
                  <div className="dash-channel-row-label">
                    <Globe size={14} className="dash-channel-row-icon-online" /> Online Store
                  </div>
                  <div className="dash-channel-row-value">
                    <CurrencyWithTooltip value={analytics?.onlineSales || 0} />
                  </div>
                  <div className="dash-channel-row-meta">
                    {analytics?.onlineOrders || 0} orders
                  </div>
                </div>
                <span className="dash-channel-row-pct">
                  {analytics?.totalRevenue > 0
                    ? Math.round((analytics?.onlineSales || 0) / analytics.totalRevenue * 100)
                    : 0}%
                </span>
              </div>

              <div className="dash-channel-row">
                <div className="dash-channel-row-body">
                  <div className="dash-channel-row-label">
                    <StoreIcon size={14} className="dash-channel-row-icon-pos" /> Point of Sale
                  </div>
                  <div className="dash-channel-row-value">
                    <CurrencyWithTooltip value={analytics?.offlineSales || 0} />
                  </div>
                  <div className="dash-channel-row-meta">
                    {analytics?.offlineOrders || 0} orders
                  </div>
                </div>
                <span className="dash-channel-row-pct">
                  {analytics?.totalRevenue > 0
                    ? Math.round((analytics?.offlineSales || 0) / analytics.totalRevenue * 100)
                    : 0}%
                </span>
              </div>
              </div>

              {/* Compact DVA Box - inside Sales Channels card */}
              <VirtualAccountCardWithGate showUpgradePrompt={false} />
            </div>
          </Card>

          {/* Inventory Health */}
          <Card>
            <Card.Header>
              <Card.Title>Inventory Health</Card.Title>
              <Card.Description>Stock status overview</Card.Description>
            </Card.Header>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <div style={{
                padding: 'var(--space-3)',
                backgroundColor: '#fef2f2',
                borderRadius: 'var(--radius-md)',
                border: '1px solid #fecaca'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: '#991b1b', fontWeight: 'var(--font-weight-medium)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <AlertTriangle size={14} color="#dc2626" /> Out of Stock
                  </span>
                  <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)', color: '#dc2626' }}>
                    {products?.filter(p => (p.stock || p.quantity || 0) === 0).length || 0}
                  </span>
                </div>
              </div>

              <div style={{
                padding: 'var(--space-3)',
                backgroundColor: '#fef9f2',
                borderRadius: 'var(--radius-md)',
                border: '1px solid #fed7aa'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: '#92400e', fontWeight: 'var(--font-weight-medium)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Package size={14} color="#f59e0b" /> Low Stock (&lt;10)
                  </span>
                  <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)', color: '#f59e0b' }}>
                    {products?.filter(p => {
                      const qty = p.stock || p.quantity || 0;
                      return qty > 0 && qty < 10;
                    }).length || 0}
                  </span>
                </div>
              </div>

              <div style={{
                padding: 'var(--space-3)',
                backgroundColor: '#f0fdf4',
                borderRadius: 'var(--radius-md)',
                border: '1px solid #bbf7d0'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: '#14532d', fontWeight: 'var(--font-weight-medium)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <PackageCheck size={14} color="#10b981" /> In Stock
                  </span>
                  <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)', color: '#10b981' }}>
                    {products?.filter(p => (p.stock || p.quantity || 0) >= 10).length || 0}
                  </span>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.href = '/products'}
                style={{ marginTop: 'var(--space-2)' }}
              >
                Manage Inventory
              </Button>
            </div>
          </Card>

          {/* Financial Summary */}
          <Card>
            <Card.Header>
              <Card.Title>Financial Summary</Card.Title>
              <Card.Description>Key financial metrics</Card.Description>
            </Card.Header>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-600)', marginBottom: 'var(--space-1)' }}>
                  Gross Profit
                </div>
                <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)', color: '#10b981' }}>
                  <CurrencyWithTooltip value={analytics?.grossProfit || 0} />
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)', marginTop: 'var(--space-1)' }}>
                  Revenue - COGS
                </div>
              </div>

              <div style={{
                padding: 'var(--space-3)',
                backgroundColor: 'var(--color-gray-50)',
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Truck size={14} color="var(--color-gray-500)" /> Shipping Costs
                </span>
                <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-900)' }}>
                  <CurrencyWithTooltip value={analytics?.totalShippingSpend || 0} />
                </span>
              </div>

              <div style={{
                padding: 'var(--space-3)',
                backgroundColor: 'var(--color-gray-50)',
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Tag size={14} color="var(--color-gray-500)" /> Discounts Given
                </span>
                <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-900)' }}>
                  <CurrencyWithTooltip value={analytics?.totalDiscountGiven || 0} />
                </span>
              </div>

              <div style={{
                padding: '12px 14px',
                backgroundColor: 'var(--color-gray-50)',
                border: '1px solid var(--color-gray-200)',
                borderRadius: '10px',
                marginTop: '8px'
              }}>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-600)', marginBottom: 'var(--space-1)' }}>
                  Net Profit
                </div>
                <div style={{
                  fontSize: 'var(--font-size-3xl)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: (analytics?.netProfit || 0) > 0 ? '#10b981' : '#ef4444'
                }}>
                  <CurrencyWithTooltip value={analytics?.netProfit || 0} />
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="dashboard-main-grid">
          {/* Business Overview */}
          <Card>
            <Card.Header>
              <Card.Title>Business Overview</Card.Title>
              <Card.Description>Key metrics and performance indicators</Card.Description>
            </Card.Header>

            {/* Removed the duplicate Total Revenue + Total Orders cards — they
                already render in the hero stat row at the top of the page.
                Recent Orders below is the actual unique content of this card. */}

            {/* Order Status Breakdown */}
            {analytics?.orderStatusBreakdown && Object.keys(analytics.orderStatusBreakdown).length > 0 && (
              <div style={{ marginBottom: 'var(--space-6)' }}>
                <div style={{ 
                  fontSize: 'var(--font-size-sm)', 
                  color: 'var(--color-gray-600)',
                  marginBottom: 'var(--space-3)',
                  fontWeight: 'var(--font-weight-medium)'
                }}>
                  Order Status Breakdown
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                  {Object.entries(analytics.orderStatusBreakdown).map(([status, count]) => (
                    <div key={status} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      padding: 'var(--space-2) var(--space-3)',
                      backgroundColor: 'var(--color-gray-50)',
                      borderRadius: 'var(--radius-md)',
                      border: `2px solid ${getStatusColor(status)}20`
                    }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(status)
                      }}></div>
                      <span style={{ 
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-gray-700)',
                        textTransform: 'capitalize'
                      }}>
                        {status}: {count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Orders */}
            <div className="recent-orders-section">
              <div className="recent-orders-header">
                <div>
                  <div className="recent-orders-title">Recent orders</div>
                  <div className="recent-orders-subtitle">Latest transactions</div>
                </div>
                <div className="recent-orders-count">
                  {orders?.length || 0} total
                </div>
              </div>

              {orders && orders.length > 0 ? (
                <div className="recent-orders-list">
                  {orders.slice(0, 5).map((order, index) => {
                    const statusColors = {
                      delivered: { bg: '#10b98115', border: '#10b981', text: '#10b981', icon: '✓' },
                      shipped: { bg: '#3b82f615', border: '#3b82f6', text: '#3b82f6', icon: '🚚' },
                      processing: { bg: '#f59e0b15', border: '#f59e0b', text: '#f59e0b', icon: '⏳' },
                      confirmed: { bg: '#8b5cf615', border: '#8b5cf6', text: '#8b5cf6', icon: '✓' },
                      pending: { bg: '#6b728015', border: '#6b7280', text: '#6b7280', icon: '○' },
                      cancelled: { bg: '#ef444415', border: '#ef4444', text: '#ef4444', icon: '✕' }
                    };
                    const status = order.status || 'pending';
                    const statusStyle = statusColors[status] || statusColors.pending;

                    return (
                      <div
                        key={order.id || index}
                        className="recent-order-item"
                        style={{
                          '--status-bg': statusStyle.bg,
                          '--status-border': statusStyle.border,
                          '--status-text': statusStyle.text,
                        }}
                        onClick={() => {
                          const id = order.id;
                          window.location.href = id ? `/orders?orderId=${encodeURIComponent(id)}` : '/orders';
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="recent-order-icon" aria-hidden="true">
                          {statusStyle.icon}
                        </div>

                        <div className="recent-order-info">
                          <div className="recent-order-headline">
                            <span className="recent-order-number">
                              #{order.orderNumber || order.id?.slice(-8) || `ORD-${index + 1}`}
                            </span>
                            <span className="recent-order-status">{status}</span>
                          </div>
                          <div className="recent-order-meta">
                            <span className="recent-order-customer">
                              {order.customerName || order.customerId?.firstName || 'Guest'}
                            </span>
                            <span className="recent-order-dot">•</span>
                            <span className="recent-order-date">
                              {new Date(order.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                        </div>

                        <div className="recent-order-amount">
                          <div className="recent-order-total">
                            {formatMoney(order.total || order.totalAmount || 0)}
                          </div>
                          <div className="recent-order-view">
                            <span>View</span>
                            <span className="recent-order-arrow">→</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{
                  padding: '32px 16px',
                  textAlign: 'center',
                  backgroundColor: 'var(--color-gray-50)',
                  borderRadius: '10px',
                  border: '1px dashed var(--color-gray-300)'
                }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    margin: '0 auto 14px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-primary-lighter)',
                    color: 'var(--color-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <ClipboardList size={24} strokeWidth={1.75} />
                  </div>
                  <div style={{
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: 'var(--color-gray-900)',
                    marginBottom: 'var(--space-2)'
                  }}>
                    No orders yet
                  </div>
                  <div style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-gray-500)',
                    maxWidth: '300px',
                    margin: '0 auto'
                  }}>
                    Your recent orders will appear here once customers start placing orders
                  </div>
                </div>
              )}
            </div>
            
            <Card.Footer>
              <Button 
                variant="primary"
                onClick={() => window.location.href = '/orders'}
              >
                View All Orders
              </Button>
            </Card.Footer>
          </Card>

          {/* Quick Actions */}
          <Card>
            <Card.Header>
              <Card.Title>Quick Actions</Card.Title>
              <Card.Description>Common tasks and shortcuts</Card.Description>
            </Card.Header>

            <div className="dash-quick-list">
              <QuickActionRow
                to="/manage-store/information"
                icon={<StoreIcon size={18} strokeWidth={2} />}
                title="Manage store"
                subtitle="Update store settings"
                tint="primary"
              />
              <QuickActionRow
                to="/products"
                icon={<Package size={18} strokeWidth={2} />}
                title="Manage products"
                subtitle={`${products?.length || 0} items in catalog`}
                tint="primary"
              />
              {store?.slug && (
                <QuickActionRow
                  href={getStoreUrl(store.slug)}
                  icon={<ExternalLink size={18} strokeWidth={2} />}
                  title="View storefront"
                  subtitle="Opens in a new tab"
                  tint="blue"
                />
              )}
              <QuickActionRow
                to="/orders"
                icon={<ClipboardList size={18} strokeWidth={2} />}
                title="Manage orders"
                subtitle={`${orders?.length || 0} total orders`}
                tint="orange"
              />
              <QuickActionRow
                to="/customers"
                icon={<Users size={18} strokeWidth={2} />}
                title="View customers"
                subtitle={`${customers?.length || 0} total customers`}
                tint="pink"
              />
            </div>

            {/* Top Products */}
            {(products && products.length > 0) && (
              <div style={{ marginTop: 'var(--space-6)', paddingTop: 'var(--space-6)', borderTop: '1px solid var(--color-gray-200)' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 'var(--space-3)'
                }}>
                  <div style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-gray-700)',
                    fontWeight: 'var(--font-weight-semibold)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Star size={14} color="#f59e0b" /> Recent Products
                  </div>
                  <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-gray-500)'
                  }}>
                    {products.length} total
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  {products.slice(0, 4).map((product, index) => (
                    <div key={product.id || index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 'var(--space-3)',
                      backgroundColor: 'white',
                      borderRadius: 'var(--radius-md)',
                      border: '2px solid var(--color-gray-100)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#10b981';
                      e.currentTarget.style.transform = 'translateX(2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-gray-100)';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                    onClick={() => window.location.href = '/products'}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-gray-900)',
                          fontWeight: 'var(--font-weight-medium)',
                          marginBottom: '2px'
                        }}>
                          {product.name || 'Unnamed Product'}
                        </div>
                        <div style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-gray-500)'
                        }}>
                          {formatMoney(product.price || 0)} • Stock: {product.stock || product.quantity || 0}
                        </div>
                      </div>
                      <div style={{
                        padding: '4px 8px',
                        borderRadius: 'var(--radius-sm)',
                        backgroundColor: (() => {
                          const qty = product.stock || product.quantity || 0;
                          return qty > 10 ? '#10b98115' : qty > 0 ? '#f59e0b15' : '#ef444415';
                        })(),
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: (() => {
                          const qty = product.stock || product.quantity || 0;
                          return qty > 10 ? '#10b981' : qty > 0 ? '#f59e0b' : '#ef4444';
                        })()
                      }}>
                        {(() => {
                          const qty = product.stock || product.quantity || 0;
                          return qty > 10 ? 'In Stock' : qty > 0 ? 'Low' : 'Out';
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* AI Business Intelligence card removed — AI lives behind the
            floating sparkle button + /ai-analytics route, not as a Dashboard
            embed. */}
      </div>

      {/* Store Creation Modal */}
      <Modal
        isOpen={showStoreModal}
        onClose={() => {
          setShowStoreModal(false);
          setStoreSlug('');
          setSlugError('');
        }}
        title="Create Your Store"
        size="md"
      >
        <div style={{ padding: 'var(--space-4)' }}>
          <p style={{
            fontSize: 'var(--font-size-base)',
            color: 'var(--color-gray-600)',
            marginBottom: 'var(--space-6)',
            lineHeight: '1.5'
          }}>
            Choose a unique URL for your storefront. This will be the web address where customers can find and shop from your store.
          </p>

          <div style={{ marginBottom: 'var(--space-6)' }}>
            <label style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--color-gray-700)',
              marginBottom: 'var(--space-2)'
            }}>
              Store Slug *
            </label>

            <div style={{ position: 'relative' }}>
              <Input
                type="text"
                value={storeSlug}
                onChange={handleSlugChange}
                onBlur={handleSlugBlur}
                placeholder="my-awesome-store"
                disabled={isCreatingStore}
                style={{
                  borderColor: slugError ? '#ef4444' : (storeSlug && !slugError && !isCheckingSlug) ? '#10b981' : undefined
                }}
              />
              {isCheckingSlug && (
                <div style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--color-gray-500)',
                  fontSize: 'var(--font-size-sm)'
                }}>
                  Checking...
                </div>
              )}
            </div>

            {slugError && (
              <p style={{
                fontSize: 'var(--font-size-sm)',
                color: '#ef4444',
                marginTop: 'var(--space-2)'
              }}>
                {slugError}
              </p>
            )}

            {storeSlug && !slugError && !isCheckingSlug && (
              <p style={{
                fontSize: 'var(--font-size-sm)',
                color: '#10b981',
                marginTop: 'var(--space-2)'
              }}>
                ✓ This slug is available!
              </p>
            )}

            <p style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-gray-500)',
              marginTop: 'var(--space-2)'
            }}>
              Your store will be available at: <strong>{getStoreUrl(storeSlug || 'your-slug')}</strong>
            </p>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <Button
              variant="outline"
              onClick={() => {
                setShowStoreModal(false);
                setStoreSlug('');
                setSlugError('');
              }}
              disabled={isCreatingStore}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateStore}
              disabled={isCreatingStore || !storeSlug || !!slugError || isCheckingSlug}
            >
              {isCreatingStore ? 'Creating Store...' : 'Create Store'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Store Activation Sidebar - shows for new stores */}
      {showActivationSidebar && store && (
        <StoreActivationSidebar
          store={store}
          onClose={() => setShowActivationSidebar(false)}
        />
      )}
    </Shell>
  );
}


